/**
 * App-session client (#469).
 *
 * The id_token → app-session exchange for this app. Two sanctioned callers:
 *   1. App.tsx, when `handleRedirectPromise()` resolves non-null — i.e. on
 *      the return trip from an explicit Microsoft loginRedirect — so a plain
 *      load can never silently re-auth a stale cached MSAL account.
 *   2. useSurvey's version-snapshot repair: a SILENT acquireTokenSilent token
 *      re-exchanges the session cookie after it expires mid-use.
 *      acquireTokenSilent only succeeds for a valid cached account, so this
 *      path still cannot resurrect a signed-out session.
 */

import { apiConfig } from '../config/apiConfig';
import { authStore, type AuthUser } from '../stores/auth';
import { DEFAULT_AUTH_BLOCK_COOLDOWN_MS, markAuthBlockedFor } from './authBlockState';

/** Upper bound for the retry hold-down, even when Retry-After is huge. */
const MAX_AUTH_BLOCK_COOLDOWN_MS = 5 * 60_000;

/**
 * Exchange an MSAL id_token for the 30-day sliding app session. On success the API has
 * set the httpOnly session cookie and the returned user is seeded into
 * app auth store. Throws when the exchange fails.
 */
export async function exchangeIdTokenForSession(idToken: string): Promise<AuthUser> {
  const res = await fetch(`${apiConfig.baseURL}/auth/entra/callback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ idToken }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;

    // The API only ever rejects this route with JSON errors (400/401/500, plus
    // a JSON 429 from its own limiter). A 403/429 with no JSON error body — or
    // Cloudflare's cf-mitigated header — means a network security layer refused
    // the request before it reached the app. Record a hold-down so LoginPage
    // doesn't let an immediate retry re-trip the same per-IP rule.
    const mitigated = res.headers.get('cf-mitigated');
    if (mitigated || res.status === 429 || (res.status === 403 && !body?.error)) {
      const retryAfterSec = Number(res.headers.get('retry-after'));
      const cooldownMs = Number.isFinite(retryAfterSec) && retryAfterSec > 0
        ? Math.min(retryAfterSec * 1000, MAX_AUTH_BLOCK_COOLDOWN_MS)
        : DEFAULT_AUTH_BLOCK_COOLDOWN_MS;
      markAuthBlockedFor(cooldownMs);
      throw new Error(
        `the request was blocked before reaching the app (HTTP ${res.status}` +
          `${mitigated ? `, cf-mitigated: ${mitigated}` : ''}). ` +
          'A network security layer is filtering or rate-limiting this network — ' +
          'wait about a minute, then reload and try again.'
      );
    }
    throw new Error(body?.error ?? `Session exchange failed (${res.status})`);
  }
  const data = (await res.json()) as { user: AuthUser };
  authStore.setAuth(data.user);
  return data.user;
}

/**
 * Ask the API to delete the server-side session and clear the httpOnly cookie
 * (JS cannot clear an httpOnly cookie itself). Best-effort: the session
 * self-expires after 8h even if this call never lands.
 */
export async function clearServerSession(): Promise<void> {
  try {
    await fetch(`${apiConfig.baseURL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch { /* network down — local clear is the load-bearing step */ }
}
